/**
 * Role-to-permission mapping matrix.
 *
 * This is the single source of truth for what each role can do.
 * Permissions apply uniformly to all drug schedules.
 */

import type { Role, PermissionAccess } from "./types";
import type { Permission } from "./permissions";

// -------------------------------------------------------
// Permission Rule: always a static access string
// -------------------------------------------------------

export type PermissionRule = PermissionAccess;

// -------------------------------------------------------
// Shorthand constants
// -------------------------------------------------------

const A: PermissionRule = "allowed";
const D: PermissionRule = "denied";
const R: PermissionRule = "requires_approval";

// -------------------------------------------------------
// Full Admin permissions (shared by admin & supervising_pharmacist)
// -------------------------------------------------------

const FULL_ACCESS: Record<Permission, PermissionRule> = {
  "users:view": A,
  "users:create": A,
  "users:deactivate": A,
  "inventory:view": A,
  "inventory:add_product": A,
  "inventory:edit": A,
  "inventory:delete_product": A,
  "dispenses:view": A,
  "dispenses:create": A,
  "dispenses:edit": A,
  "dispenses:delete": A,
  "receipts:view": A,
  "receipts:create": A,
  "receipts:edit": A,
  "receipts:delete": A,
  "adjustments:create": A,
  "audits:create": A,
  "reports:view": A,

  "reports:delete": A,
  "audit_log:view": A,
  "settings:general": A,
  "settings:pharmacy": A,
  "settings:subscription": A,
  "reminders:view": A,
  "reminders:create": A,
  "reminders:edit": A,
  "reminders:delete": A,
  "reminders:complete": A,
  "analytics:view": A,
  "alerts:view": A,
  "activity:view": A,
  "patients:view": A,
  "patients:create": A,
  "patients:edit": A,
  "patients:delete": A,
};

// -------------------------------------------------------
// Permission Matrix
// -------------------------------------------------------

export const PERMISSION_MATRIX: Record<
  Role,
  Record<Permission, PermissionRule>
> = {
  admin: { ...FULL_ACCESS },
  supervising_pharmacist: { ...FULL_ACCESS },

  pharmacist: {
    "users:view": A,
    "users:create": D,
    "users:deactivate": D,
    "inventory:view": A,
    "inventory:add_product": A,
    "inventory:edit": A,
    "inventory:delete_product": R,
    "dispenses:view": A,
    "dispenses:create": A,
    "dispenses:edit": A,
    "dispenses:delete": A,
    "receipts:view": A,
    "receipts:create": A,
    "receipts:edit": A,
    "receipts:delete": A,
    "adjustments:create": A,
    "audits:create": A,
    "reports:view": A,

    "reports:delete": A,
    "audit_log:view": A,
    "settings:general": D,
    "settings:pharmacy": A,
    "settings:subscription": D,
    "reminders:view": A,
    "reminders:create": A,
    "reminders:edit": A,
    "reminders:delete": A,
    "reminders:complete": A,
    "analytics:view": A,
    "alerts:view": A,
    "activity:view": A,
    "patients:view": A,
    "patients:create": A,
    "patients:edit": A,
    "patients:delete": A,
  },

  pharmacy_intern: {
    "users:view": A,
    "users:create": D,
    "users:deactivate": D,
    "inventory:view": A,
    "inventory:add_product": A,
    "inventory:edit": A,
    "inventory:delete_product": D,
    "dispenses:view": A,
    "dispenses:create": A,
    "dispenses:edit": A,
    "dispenses:delete": R,
    "receipts:view": A,
    "receipts:create": A,
    "receipts:edit": A,
    "receipts:delete": R,
    "adjustments:create": R,
    "audits:create": A,
    "reports:view": A,

    "reports:delete": R,
    "audit_log:view": A,
    "settings:general": D,
    "settings:pharmacy": A,
    "settings:subscription": D,
    "reminders:view": A,
    "reminders:create": A,
    "reminders:edit": A,
    "reminders:delete": A,
    "reminders:complete": A,
    "analytics:view": A,
    "alerts:view": A,
    "activity:view": A,
    "patients:view": A,
    "patients:create": A,
    "patients:edit": A,
    "patients:delete": R,
  },

  technician: {
    "users:view": D,
    "users:create": D,
    "users:deactivate": D,
    "inventory:view": A,
    "inventory:add_product": A,
    "inventory:edit": A,
    "inventory:delete_product": D,
    "dispenses:view": A,
    "dispenses:create": A,
    "dispenses:edit": A,
    "dispenses:delete": R,
    "receipts:view": A,
    "receipts:create": A,
    "receipts:edit": A,
    "receipts:delete": R,
    "adjustments:create": R,
    "audits:create": A,
    "reports:view": A,

    "reports:delete": D,
    "audit_log:view": A,
    "settings:general": D,
    "settings:pharmacy": A,
    "settings:subscription": D,
    "reminders:view": A,
    "reminders:create": A,
    "reminders:edit": A,
    "reminders:delete": A,
    "reminders:complete": A,
    "analytics:view": D,
    "alerts:view": A,
    "activity:view": A,
    "patients:view": A,
    "patients:create": A,
    "patients:edit": A,
    "patients:delete": R,
  },

  auditor: {
    "users:view": D,
    "users:create": D,
    "users:deactivate": D,
    "inventory:view": A,
    "inventory:add_product": D,
    "inventory:edit": D,
    "inventory:delete_product": D,
    "dispenses:view": A,
    "dispenses:create": D,
    "dispenses:edit": D,
    "dispenses:delete": D,
    "receipts:view": A,
    "receipts:create": D,
    "receipts:edit": D,
    "receipts:delete": D,
    "adjustments:create": D,
    "audits:create": D,
    "reports:view": A,

    "reports:delete": D,
    "audit_log:view": A,
    "settings:general": D,
    "settings:pharmacy": A,
    "settings:subscription": D,
    "reminders:view": D,
    "reminders:create": D,
    "reminders:edit": D,
    "reminders:delete": D,
    "reminders:complete": D,
    "analytics:view": A,
    "alerts:view": A,
    "activity:view": A,
    "patients:view": A,
    "patients:create": D,
    "patients:edit": D,
    "patients:delete": D,
  },
};
